import { ApolloError } from '@apollo/client'
import {
  calculateDiscount,
  capitalizeCase,
  formatCurrency,
  formatToTwoDecimals,
  hasAdminAccess,
  minutesToDuration,
  removeUnderScore
} from '.'
import {
  AccountType,
  BillingType,
  BillStatus,
  CaseBilling,
  CreateManualEntryInput,
  Entry,
  EntryType,
  FetchBillPaymentsQuery,
  FetchQuickBillDetailsQuery,
  GenerateQuickBillInput,
  PayableStatus,
  PaymentAllocation,
  PaymentAllocationBalanceEffect,
  ReportStatus,
  TransactionStatus,
  UpdateBillRequestInput,
  UpdateEntryInput,
  User
} from '../../generated'
import {
  BAD_USER_INPUT,
  BILL_VIEW_ROUTE,
  FINAL_BILL_STATUSES,
  PRE_BILL_STATUSES,
  SOMETHING_WENT_WRONG,
  VOID_BILL_STATUSES
} from '../constants'
import { EditBillEntryForm, EditBillForm } from '../interfaceTypes/forms'
import { CalculateClientBillDataParams, CalculateClientBillDataResult } from '../interfaceTypes'
import { format } from 'date-fns'
import {
  FormatUpdateBillMutationDataParams,
  FormatQuickBillMutationDataParams,
  FormatBillEntriesByDateRow,
  BillItem,
  BillEntriesSubtotal
} from '../interfaceTypes/billing'

export const formatStatus = (status: string) => {
  if (status === BillStatus.ApprovedByResponsibleAttorney) return "Awaiting Dept Head's Approval"

  return capitalizeCase(removeUnderScore(status.toLowerCase()))
}

export const statusColors: Record<string, string> = {
  [BillStatus.Draft]: '#ED6C02',
  [BillStatus.SentForApproval]: '#0288D1',
  [BillStatus.ApprovedByResponsibleAttorney]: '#26A69A',
  [BillStatus.Billed]: '#ED6C02',
  [BillStatus.SentToClient]: '#ED6C02',
  [BillStatus.PartiallyPaid]: '#62CAF4',
  [BillStatus.Paid]: '#13BB00',
  [BillStatus.PrePaid]: '#13BB00',
  [BillStatus.Void]: '#9E9E9E',
  [BillStatus.Forwarded]: '#9E9E9E',
  [BillStatus.WrittenOff]: '#9E9E9E',
  [BillStatus.Archived]: '#9E9E9E',
  [TransactionStatus.SettledSuccessfully]: '#ED6C02',
  [TransactionStatus.SettlementError]: '#ED6C02',
  [TransactionStatus.RefundSettledSuccessfully]: '#ED6C02',
  [TransactionStatus.RefundPendingSettlement]: '#9E9E9E',
  [TransactionStatus.CapturedPendingSettlement]: '#9E9E9E',
  [TransactionStatus.FdsPendingReview]: '#9E9E9E',
  [ReportStatus.Completed]: '#26A69A',
  [ReportStatus.Failed]: '#D32F2F',
  [ReportStatus.InProgress]: '#ED6C02',
  [ReportStatus.Cancelled]: '#757575'
}

export const payableStatusColors: Record<string, string> = {
  [PayableStatus.Unpaid]: '#ED6C02',
  [PayableStatus.Paid]: '#13BB00',
  [PayableStatus.Voided]: '#9E9E9E'
}

const entryTypesOrder: Record<EntryType, number> = {
  [EntryType.Time]: 0,
  [EntryType.Legal]: 1,
  [EntryType.Expense]: 2,
  [EntryType.Cost]: 3,
  [EntryType.NonClient]: 4
}

const entryTypesLabel: Record<EntryType, string> = {
  [EntryType.Time]: 'Attorney/Paralegal Time',
  [EntryType.Legal]: 'Legal fees',
  [EntryType.Expense]: 'Expense',
  [EntryType.Cost]: 'Investigator Time',
  [EntryType.NonClient]: 'Non-Client Time'
}

export const convertDurationToHours = (duration: string): string => {
  if (!duration) return '0'

  // Duration format: hh:mm:ss
  const parts = duration.split(':')
  if (parts.length < 2) return '0'

  const hours = parseInt(parts[0]) || 0
  const minutes = parseInt(parts[1]) || 0
  const seconds = parseInt(parts[2]) || 0
  const minutesInHours = minutes / 60
  const secondsInHours = seconds / 3600

  return (hours + minutesInHours + secondsInHours).toFixed(2)
}

export const calculateEntryTotal = (entry: Partial<Entry>) => {
  if (!entry?.billable) return 0

  if (entry.entryType === EntryType.Time || entry.entryType === EntryType.Cost) {
    const totalHours = convertDurationToHours(entry.duration || '')
    return Number(totalHours) * Number(entry.rate || 0)
  }

  return Number(entry.amount || 0)
}

export const processEntries = (
  entries: FetchQuickBillDetailsQuery['fetchQuickBillDetails']['entries']
): Omit<EditBillForm, 'discountPercentage'> => {
  const timeEntries: EditBillEntryForm[] = []
  const legalFeeEntries: EditBillEntryForm[] = []
  const expenseEntries: EditBillEntryForm[] = []
  const investigatorEntries: EditBillEntryForm[] = []

  entries.forEach(entry => {
    if (!entry) return

    const elapsedTime = entry.duration
    const duration = typeof elapsedTime === 'number' ? minutesToDuration(elapsedTime) : elapsedTime
    const entryForm: EditBillEntryForm = {
      id: entry.id,
      entryType: entry.entryType!,
      date: new Date(entry.date),
      userId: entry.user?.id,
      description: entry.description,
      categoryId: entry.category?.id,
      feeTypeId: entry.feeType?.id,
      duration,
      rate: entry.rate || 0,
      amount: entry.amount || 0,
      qty: entry.qty ?? null,
      unitPrice: entry.unitPrice ?? null,
      billable: entry.billable,
      showOnBill: entry.showOnBill,
      sendNotesToMatterNotes: entry.sendNotesToMatterNotes ?? false,
      caseId: entry.caseId,
      creatorId: entry.creatorId,
      isSystemGenerated: entry.isSystemGenerated
    }

    switch (entry.entryType) {
      case EntryType.Time:
        timeEntries.push(entryForm)
        break
      case EntryType.Legal:
        legalFeeEntries.push(entryForm)
        break
      case EntryType.Expense:
        expenseEntries.push(entryForm)
        break
      case EntryType.Cost:
        investigatorEntries.push(entryForm)
        break
    }
  })

  return {
    timeEntries,
    legalFeeEntries,
    expenseEntries,
    investigatorEntries
  }
}

export const calculateLegalWorkAmount = (entries: Partial<Entry>[]): number => {
  const legalWorkEntries = entries.filter(
    entry => entry.entryType === EntryType.Time || entry.entryType === EntryType.Legal
  )

  return legalWorkEntries.reduce((total, entry) => {
    return total + calculateEntryTotal(entry)
  }, 0)
}

export const calculateNetTotal = (bill: EditBillForm): { netTotal: number; discount: number } => {
  let discount = 0
  const timeTotal = bill.timeEntries.reduce((total, entry) => {
    return total + calculateEntryTotal(entry)
  }, 0)
  const legalTotal = bill.legalFeeEntries.reduce((total, entry) => {
    return total + calculateEntryTotal(entry)
  }, 0)
  const expenseTotal = bill.expenseEntries.reduce((total, entry) => {
    return total + calculateEntryTotal(entry)
  }, 0)
  const investigatorTotal = bill.investigatorEntries.reduce((total, entry) => {
    return total + calculateEntryTotal(entry)
  }, 0)

  if (bill.discountPercentage) {
    const legalWorkTotal = calculateLegalWorkAmount([...bill.timeEntries, ...bill.legalFeeEntries])
    discount = calculateDiscount(legalWorkTotal, bill.discountPercentage)
  }

  return {
    netTotal: timeTotal + legalTotal + expenseTotal + investigatorTotal - discount,
    discount
  }
}

export const formatUpdateBillMutationData = (params: FormatUpdateBillMutationDataParams): UpdateBillRequestInput => {
  const { bill, data, totalAmount, discount } = params
  const { timeEntries, legalFeeEntries, expenseEntries, investigatorEntries, discountPercentage } = data
  const entries = [...timeEntries, ...legalFeeEntries, ...expenseEntries, ...investigatorEntries]

  const newEntries: CreateManualEntryInput[] = []
  const updatedEntries: UpdateEntryInput[] = []
  const billingNotes: string[] = []

  entries.forEach(entry => {
    // `sendNotesToMatterNotes` drives the bill's billing notes AND is persisted
    // on the entry itself, so it must stay in the entry payload — stripping it
    // here is what made the checkbox reset every time the bill was reopened.
    const { id, isNew, isSystemGenerated, ...restEntry } = entry

    if (entry.sendNotesToMatterNotes && entry.description) {
      billingNotes.push(entry.description)
    }

    if (isNew) {
      newEntries.push({
        ...restEntry,
        rate: restEntry.rate || 0
      })
    } else {
      updatedEntries.push({
        entryId: id!,
        ...restEntry
      })
    }
  })

  const currentEntryIds = bill.billItems?.map(item => item.entry?.id).filter(Boolean) || []
  const newEntryIds = new Set(entries.map(entry => entry.id).filter(Boolean))
  const deletedEntries = currentEntryIds.filter(id => !newEntryIds.has(id))

  return {
    billId: bill.id,
    entries: {
      new: newEntries,
      updated: updatedEntries,
      deleted: deletedEntries
    },
    totalAmount,
    discount,
    discountPercentage,
    billingNotes
  }
}

export const formatQuickBillMutationData = (params: FormatQuickBillMutationDataParams): GenerateQuickBillInput => {
  const { caseId, data } = params
  const { timeEntries, legalFeeEntries, expenseEntries, investigatorEntries, discountPercentage } = data
  const entries = [...timeEntries, ...legalFeeEntries, ...expenseEntries, ...investigatorEntries]

  const newEntries: CreateManualEntryInput[] = []
  const updatedEntries: UpdateEntryInput[] = []
  const billingNotes: string[] = []

  entries.forEach(entry => {
    // `sendNotesToMatterNotes` drives the bill's billing notes AND is persisted
    // on the entry itself, so it must stay in the entry payload — stripping it
    // here is what made the checkbox reset every time the bill was reopened.
    const { id, isNew, isSystemGenerated, ...restEntry } = entry

    if (entry.sendNotesToMatterNotes && entry.description) {
      billingNotes.push(entry.description)
    }

    // Convert amount and rate to numbers if they are strings
    const amount =
      typeof restEntry.amount === 'string' ? (restEntry.amount ? parseFloat(restEntry.amount) : null) : restEntry.amount
    const rate = typeof restEntry.rate === 'string' ? parseFloat(restEntry.rate) || 0 : restEntry.rate || 0

    if (isNew) {
      newEntries.push({
        ...restEntry,
        rate,
        amount
      })
    } else {
      updatedEntries.push({
        entryId: id!,
        ...restEntry,
        rate,
        amount
      })
    }
  })

  return {
    caseId,
    entries: {
      new: newEntries,
      updated: updatedEntries
    },
    discountPercentage,
    billingNotes
  }
}

export const canEditBill = (
  billApproverId: string | null,
  isDepartmentHead: boolean | null,
  billStatus: BillStatus,
  user: User
) => {
  if (
    hasAdminAccess(user) &&
    (billStatus === BillStatus.Draft ||
      billStatus === BillStatus.SentForApproval ||
      billStatus === BillStatus.ApprovedByResponsibleAttorney)
  ) {
    return true
  }

  if (billApproverId === user.id && (billStatus === BillStatus.Draft || billStatus === BillStatus.SentForApproval)) {
    return true
  }

  if (isDepartmentHead && billStatus === BillStatus.ApprovedByResponsibleAttorney) {
    return true
  }

  return false
}

export const canViewBillActions = (
  billApproverId: string | null,
  isDepartmentHead: boolean | null | undefined,
  billStatus: BillStatus,
  user: User
) => {
  if (hasAdminAccess(user) && !VOID_BILL_STATUSES.includes(billStatus)) {
    return true
  }

  if (billApproverId === user.id && (billStatus === BillStatus.Draft || billStatus === BillStatus.SentForApproval)) {
    return true
  }

  if (isDepartmentHead && billStatus === BillStatus.ApprovedByResponsibleAttorney) {
    return true
  }

  return false
}

export const canEditFundRequest = (billStatus: BillStatus, user: User) => {
  if (hasAdminAccess(user) && billStatus === BillStatus.Billed) {
    return true
  }

  return false
}

export const canViewFundRequestActions = (billStatus: BillStatus, user: User) => {
  if (hasAdminAccess(user) && !VOID_BILL_STATUSES.includes(billStatus)) {
    return true
  }

  return false
}

export const canWriteOffBill = (billStatus: BillStatus, user: User | null): boolean => {
  if (!user?.id || !billStatus) return false

  const finalBillStatuses: BillStatus[] = [
    ...FINAL_BILL_STATUSES.filter(status => status !== BillStatus.Paid && status !== BillStatus.PrePaid)
  ]
  if (hasAdminAccess(user) && finalBillStatuses.includes(billStatus)) {
    return true
  }

  return false
}

// Carrying forward interest only makes sense on a live final bill (interest never applies to
// pre-bills, and voided/archived bills can't receive it). Server also gates on CARRY_FORWARD_INTEREST.
export const canCarryForwardInterest = (billStatus: BillStatus | null | undefined, user: User | null): boolean => {
  if (!user?.id || !billStatus) return false

  const eligible: BillStatus[] = [
    BillStatus.Billed,
    BillStatus.SentToClient,
    BillStatus.PartiallyPaid,
    BillStatus.Forwarded
  ]
  return !!hasAdminAccess(user) && eligible.includes(billStatus)
}

// Statuses a bill can be voided from at all.
//
// PartiallyPaid is included because a bill whose only payment was later deleted keeps that status
// (the reversal can't know what the status was beforehand), so the status alone can't tell us
// whether anything is still applied — `hasAppliedPayments` makes that call.
//
// Paid and PrePaid are deliberately EXCLUDED. A settled bill is not something anyone should be
// voiding from a menu, and the vast majority of historic paid bills carry no live allocations
// (ADV-migrated), so listing them here would put a Void action on tens of thousands of closed
// bills for no real benefit. A genuinely wrong one goes through support.
const VOIDABLE_BILL_STATUSES: BillStatus[] = [
  ...PRE_BILL_STATUSES,
  BillStatus.Billed,
  BillStatus.SentToClient,
  BillStatus.PartiallyPaid
]

/**
 * Whether the Void action should be offered at all. Deliberately does NOT look at payments — a
 * bill that still has payments applied shows Void and then explains why it's blocked, so the user
 * knows what to do instead of the action silently disappearing.
 */
export const canShowVoidBillAction = (user: User | null, status: BillStatus | null | undefined): boolean => {
  if (!user?.id || !status) return false

  return !!hasAdminAccess(user) && VOIDABLE_BILL_STATUSES.includes(status)
}

/** True when money is still applied to the bill, i.e. its balance is short of the full amount. */
export const hasAppliedPayments = (amountPaid: number | null | undefined): boolean =>
  Math.round((amountPaid || 0) * 100) > 0

/**
 * Whether a void can actually go through: right status AND nothing applied to the bill.
 *
 * Used by the BULK void action, which has no per-bill modal to explain a refusal — so it keeps the
 * original, narrower status set. The PartiallyPaid recovery case is a deliberate, one-bill-at-a-time
 * decision and belongs in the single-bill menu, not in a multi-select.
 */
export const canVoidBill = (
  user: User | null,
  status: BillStatus | null | undefined,
  amountPaid: number | null | undefined
) => {
  if (!user?.id || !status) return false

  return (
    !!hasAdminAccess(user) &&
    !hasAppliedPayments(amountPaid) &&
    (PRE_BILL_STATUSES.includes(status) || status === BillStatus.Billed || status === BillStatus.SentToClient)
  )
}

export const generateBillPaymentUrl = (token: string) => {
  return `${window.location.origin}${BILL_VIEW_ROUTE}/${token}`
}

export const calculateClientBillData = (params: CalculateClientBillDataParams): CalculateClientBillDataResult => {
  const { bill, paymentAllocations, client, showPrebillView, previousBalance } = params
  const subtotal = (bill.amount || 0) + (bill.discount || 0)

  if (!bill.status) return { subtotal: 0, payableAmount: 0, paymentAllocations: [], writtenOff: 0 }

  if (PRE_BILL_STATUSES.includes(bill.status) || bill.status === BillStatus.Void || showPrebillView) {
    const payableAmount = (previousBalance || 0) + (bill.amount || 0)
    return {
      subtotal: formatToTwoDecimals(subtotal),
      payableAmount: formatToTwoDecimals(payableAmount),
      paymentAllocations: [],
      writtenOff: 0
    }
  }

  const amountPaid = paymentAllocations?.reduce((acc, paymentAllocation) => {
    if (bill.splitBill) {
      const paymentAmount =
        paymentAllocation.client?.id === client.id
          ? paymentAllocation.balanceEffect === PaymentAllocationBalanceEffect.Increase
            ? -paymentAllocation.amount
            : paymentAllocation.amount
          : 0
      return acc + paymentAmount
    }
    return (
      acc +
      (paymentAllocation.balanceEffect === PaymentAllocationBalanceEffect.Increase
        ? -paymentAllocation.amount
        : paymentAllocation.amount)
    )
  }, 0)

  if (bill.splitBill) {
    const payableAmount =
      (previousBalance || 0) +
      (client.share || 0) +
      (bill.interestAccrued || 0) -
      (amountPaid || 0) -
      (client.writtenOff || 0)
    const clientPayments =
      paymentAllocations?.filter(paymentAllocation => paymentAllocation.client?.id === client.id) || []

    return {
      subtotal: formatToTwoDecimals(subtotal),
      payableAmount: formatToTwoDecimals(payableAmount),
      paymentAllocations: clientPayments as PaymentAllocation[],
      writtenOff: client.writtenOff
    }
  }

  // In case of flat fee matter, client can pay the additional payment for future monthly payments
  const payableAmount =
    (previousBalance || 0) +
    (bill.amount || 0) +
    (bill.interestAccrued || 0) -
    (amountPaid || 0) -
    (bill.writtenOff || 0)

  return {
    subtotal: formatToTwoDecimals(subtotal),
    payableAmount: formatToTwoDecimals(payableAmount),
    paymentAllocations: (paymentAllocations || []) as PaymentAllocation[],
    writtenOff: bill.writtenOff
  }
}

export const getGraphQLMutationErrorMessage = (error: ApolloError): string => {
  const { graphQLErrors } = error
  let message = SOMETHING_WENT_WRONG

  if (graphQLErrors) {
    graphQLErrors.map(({ extensions }) => {
      if (extensions) {
        const { code } = extensions
        if (code === BAD_USER_INPUT) {
          message = error.message
        }
      }

      return null
    })
  }

  return message
}

export const isHourlyCase = (billing: Pick<CaseBilling, 'billingType' | 'isFlatFeeHoursExhausted'>): boolean => {
  return Boolean(
    billing?.billingType === BillingType.Hourly ||
      (billing?.billingType === BillingType.Hybrid && billing?.isFlatFeeHoursExhausted)
  )
}

export const isContingencyCase = (billing: Pick<CaseBilling, 'billingType'>): boolean => {
  return billing?.billingType === BillingType.Contingency || billing?.billingType === BillingType.ContingencyHybrid
}

export const formatBillEntriesByDate = (billItems: BillItem[]): FormatBillEntriesByDateRow[] => {
  const rows: FormatBillEntriesByDateRow[] = []

  const getRate = (entry: BillItem['entry']) => {
    if (entry.entryType === EntryType.Legal || entry.entryType === EntryType.Expense) {
      return entry.amount || 0
    }

    return entry.rate || 0
  }

  const getNotes = (entry: BillItem['entry']) => {
    if (entry.entryType === EntryType.Legal) {
      return `${entry.feeType?.feeType ? `${entry.feeType?.feeType}\n` : ''}${entry.description || ''}`
    }

    if (entry.entryType === EntryType.Expense) {
      return `${entry.category?.name ? `${entry.category?.name}\n` : ''}${entry.description || ''}`
    }

    return entry.description || ''
  }

  // Sort by date
  const sortedByDate = [...billItems].sort(
    (a, b) => new Date(a.entry.date).getTime() - new Date(b.entry.date).getTime()
  )

  // Group by date
  const groupedEntries = sortedByDate.reduce((acc, item) => {
    const date = format(new Date(item.entry.date), 'MM/dd/yyyy')
    if (!acc[date]) {
      acc[date] = []
    }
    acc[date].push(item)
    return acc
  }, {} as Record<string, BillItem[]>)

  // Sort grouped entries (based on date) by entry type
  const sortedGroupedEntriesByType = Object.entries(groupedEntries).map(([date, items]) => {
    return {
      date,
      items:
        items?.sort((a, b) => {
          if (!a.entry?.entryType || !b.entry?.entryType) return 0
          return entryTypesOrder[a.entry.entryType] - entryTypesOrder[b.entry.entryType]
        }) || []
    }
  })

  sortedGroupedEntriesByType.forEach(({ date, items }) => {
    items.forEach(item => {
      const attorneyName = `${item.entry.user?.firstName || ''} ${item.entry.user?.lastName || ''}`.trim()
      const entryType = item.entry.entryType ? entryTypesLabel[item.entry.entryType] : ''
      const rate = getRate(item.entry)
      const isExpenseOrLegal = item.entry.entryType === EntryType.Expense || item.entry.entryType === EntryType.Legal
      const duration = isExpenseOrLegal ? '-' : convertDurationToHours(item.entry.duration || '01:00:00')
      const total = (item.entry.billable ? item.amount : rate * Number(duration || 0)) || 0
      const notes = getNotes(item.entry)

      rows.push({
        id: item.id,
        date,
        type: entryType,
        notes,
        attorney: attorneyName,
        duration,
        rate,
        total,
        isBillable: item.entry.billable
      })
    })
  })

  return rows
}

export const calculateBillEntriesSubtotals = (billItems: BillItem[]): BillEntriesSubtotal[] => {
  const subtotals = {
    [EntryType.Time]: { duration: 0, total: 0 },
    [EntryType.Legal]: { duration: 0, total: 0 },
    [EntryType.Expense]: { duration: 0, total: 0 },
    [EntryType.Cost]: { duration: 0, total: 0 }
  }

  billItems.forEach(item => {
    const entryType = item.entry.entryType as keyof typeof subtotals
    if (!entryType || !subtotals.hasOwnProperty(entryType)) return

    const total = (item.entry.billable ? item.amount : 0) || 0
    const duration = convertDurationToHours(item.entry.duration || '00:00:00')

    subtotals[entryType].duration += Number(duration)
    subtotals[entryType].total += total
  })

  return [
    { type: entryTypesLabel[EntryType.Time], ...subtotals[EntryType.Time] },
    { type: entryTypesLabel[EntryType.Legal], ...subtotals[EntryType.Legal] },
    { type: entryTypesLabel[EntryType.Expense], ...subtotals[EntryType.Expense] },
    { type: entryTypesLabel[EntryType.Cost], ...subtotals[EntryType.Cost] }
  ]
}

export const getTrustCheckConfrimationModalDetails = (
  payment: NonNullable<FetchBillPaymentsQuery['fetchBillPayments']['payments']>[number] | null
): { title: string; message: string } => {
  const trustCheck = payment?.account?.type === AccountType.Trust

  if (trustCheck) {
    return {
      title: `Trust Check #${payment?.referenceNumber}`,
      message: `This is the check that will be used to withdraw funds from Trust account (${payment?.linkedPayment?.account?.name} #${payment?.linkedPayment?.account?.sortCode}) and deposit into ZEscrow account (${payment?.account?.name} #${payment?.account?.sortCode}) via check #${payment?.referenceNumber}.`
    }
  }

  return {
    title: `Disbursement Check #${payment?.referenceNumber}`,
    message: `This is the check that will be used to disburse funds from Zescrow account (${payment?.linkedPayment?.account?.name} #${payment?.linkedPayment?.account?.sortCode}) into firm's Operating account (${payment?.account?.name} #${payment?.account?.sortCode}) via check #${payment?.referenceNumber}.`
  }
}

export const getRetainerPaymentInfo = (
  billing: Pick<CaseBilling, 'flatFee' | 'upfrontFlatFee' | 'monthlyFlatFee' | 'specialPayments'>
): string => {
  let info = `${formatCurrency(billing?.flatFee || 0)} flat fee plus costs; ${formatCurrency(
    billing?.upfrontFlatFee || 0
  )} initial payment; `

  if (billing?.specialPayments?.length) {
    info += `${billing.specialPayments
      .map(payment => `${formatCurrency(payment.amount)} on ${format(new Date(payment.date), 'MM/dd/yyyy')}; `)
      .join('')}`
  }

  if (billing?.monthlyFlatFee) {
    info += `${formatCurrency(billing.monthlyFlatFee)} per month until paid in full`
  }

  return info
}
